/**
 * Parametric interior layout variants.
 *
 * A variant is a STARTING POINT generated from the room's own measurements,
 * not a locked preset: every run it emits is an ordinary `BenchRun` the user
 * can move, resize or delete afterwards. That is the whole point of the
 * object model — two users with the same dimensions should be able to end up
 * with different saunas.
 *
 * `two-tier` reproduces the geometry the engines drew before the layout
 * existed (upper lav hung on the back wall at 600 deep, foot lav in front of
 * it at 400), so a design with no user layout keeps its old shape.
 */
import {
	BENCH_CORNER_MARGIN_MM,
	LOWER_BENCH_DEPTH_MM,
	UPPER_BENCH_DEPTH_MM,
} from "./constants";
import {
	type BenchRun,
	type HeaterPlacement,
	type InteriorLayout,
	type InteriorLayoutVariantId,
	type InteriorRoom,
	type InteriorWall,
	roomLengthMm,
	roomWidthMm,
	wallAxisLengthMm,
} from "./types";

export const INTERIOR_LAYOUT_VARIANT_IDS: readonly InteriorLayoutVariantId[] = [
	"straight",
	"two-tier",
	"l-shape",
	"u-shape",
] as const;

/**
 * Translation keys for the variant names. The domain never returns prose —
 * the UI resolves these at the render boundary.
 */
export const INTERIOR_LAYOUT_VARIANT_LABEL_KEYS = {
	straight: "domain_interior_variant_straight",
	"two-tier": "domain_interior_variant_twoTier",
	"l-shape": "domain_interior_variant_lShape",
	"u-shape": "domain_interior_variant_uShape",
} as const satisfies Record<InteriorLayoutVariantId, string>;

export type InteriorLayoutVariantLabelKey =
	(typeof INTERIOR_LAYOUT_VARIANT_LABEL_KEYS)[InteriorLayoutVariantId];

/**
 * The engines back the heater against the right long wall with a 150 mm gap
 * (`buildSquareInterior` / `buildTwoRoomInterior`). Exported so the engine
 * default and the layout default cannot drift apart.
 */
export const DEFAULT_HEATER_WALL_GAP_MM = 150;

function runId(wall: InteriorWall, tier: BenchRun["tier"]): string {
	return `bench-${wall}-${tier}`;
}

/**
 * A run stops short of the corners so it does not fight the adjoining wall's
 * run (and, on a single wall, so the panel and ledger have somewhere to land).
 */
function fullWallRun(
	room: InteriorRoom,
	wall: InteriorWall,
	tier: BenchRun["tier"],
	depthMm: number,
	offsetFromWallMm: number,
	startMm = 0,
	endMm = wallAxisLengthMm(room, wall),
): BenchRun {
	const axis = wallAxisLengthMm(room, wall);
	const end = Math.min(axis, endMm);
	return {
		id: runId(wall, tier),
		room: room.id,
		wall,
		tier,
		startMm,
		lengthMm: Math.max(0, end - startMm),
		depthMm,
		offsetFromWallMm,
	};
}

/**
 * Where a generated layout hangs its benches. The engines decide this per
 * design (`getHotRoomBenchPlacement`) — a two-room whose sauna sits at the +Z
 * end puts them on the FRONT wall — so it is an input here, not a constant.
 * Hard-coding "back" generated benches against the divider for those designs.
 */
export interface BenchAnchor {
	wall: InteriorWall;
	offsetFromWallMm: number;
	/**
	 * Where the main runs stop, measured along the anchor wall from its low
	 * corner — short of a door in that wall. Defaults to the full wall.
	 */
	endMm?: number;
	/**
	 * Turn BOTH tiers round the L's corner, as a built L-lav does: the upper
	 * lavar meet in the corner and the foot lav runs in front of them on both
	 * walls. Without it the side run is a single upper lav that starts beyond
	 * the whole main band.
	 */
	wrapTiers?: boolean;
}

export const DEFAULT_BENCH_ANCHOR: BenchAnchor = {
	wall: "back",
	offsetFromWallMm: 0,
};

function wallRunsAcrossX(wall: InteriorWall): boolean {
	return wall === "back" || wall === "front";
}

function sideRunDepth(room: InteriorRoom, wall: InteriorWall): number {
	// An L/U side run must leave a walkway; never let it eat more than a third
	// of the room measured away from its wall.
	const acrossMm = wallRunsAcrossX(wall)
		? roomLengthMm(room)
		: roomWidthMm(room);
	return Math.min(UPPER_BENCH_DEPTH_MM, Math.floor(acrossMm / 3));
}

/** The side walls of an L/U: the two walls meeting the anchor wall's ends. */
function sideWallsFor(anchor: BenchAnchor): [InteriorWall, InteriorWall] {
	return wallRunsAcrossX(anchor.wall) ? ["left", "right"] : ["back", "front"];
}

/** The anchor wall sits at the low-coordinate end of its side walls' axis. */
function anchorAtLowEnd(anchor: BenchAnchor): boolean {
	return anchor.wall === "back" || anchor.wall === "left";
}

/**
 * How deep the main bench band reaches from its wall — the DEEPEST run on it,
 * which in every variant that has a side run is the foot lav standing in front
 * of the upper lav. Measuring to the upper lav's depth put the side run
 * straight through the foot bench; `layoutVariants.test.ts` caught it.
 */
function sideRunBandMm(mainRuns: BenchRun[]): number {
	return mainRuns.reduce(
		(deepest, run) => Math.max(deepest, run.offsetFromWallMm + run.depthMm),
		0,
	);
}

/**
 * Side runs fill the length the main bench band leaves free. Which END that is
 * depends on the anchor wall: a back-wall band occupies the low-Z end, so the
 * side run starts after it; a front-wall band occupies the high-Z end, so the
 * side run starts at the corner margin and stops before the band.
 */
function sideRun(
	room: InteriorRoom,
	wall: InteriorWall,
	anchor: BenchAnchor,
	bandMm: number,
	tier: BenchRun["tier"] = "upper",
	depthMm = sideRunDepth(room, wall),
	offsetFromWallMm = 0,
): BenchRun {
	const freeMm = Math.max(
		0,
		wallAxisLengthMm(room, wall) - bandMm - BENCH_CORNER_MARGIN_MM,
	);
	return {
		id: runId(wall, tier),
		room: room.id,
		wall,
		tier,
		startMm: anchorAtLowEnd(anchor) ? bandMm : BENCH_CORNER_MARGIN_MM,
		lengthMm: freeMm,
		depthMm,
		offsetFromWallMm,
	};
}

/**
 * An L whose two tiers both turn the corner. The upper lavar meet in the
 * corner, so the top seat is continuous; the foot lav runs in front of them on
 * both walls, the main one starting clear of the side upper lav's depth.
 * Only the side wall at the anchor's low end is used, like the plain L.
 */
function wrappedLShape(
	room: InteriorRoom,
	anchor: BenchAnchor,
	upper: BenchRun,
	lower: BenchRun,
): BenchRun[] {
	const [sideWall] = sideWallsFor(anchor);
	const upperDepthMm = sideRunDepth(room, sideWall);
	const sideUpper = sideRun(
		room,
		sideWall,
		anchor,
		upper.offsetFromWallMm + upper.depthMm,
	);
	const sideLower = sideRun(
		room,
		sideWall,
		anchor,
		sideRunBandMm([upper, lower]),
		"lower",
		LOWER_BENCH_DEPTH_MM,
		upperDepthMm,
	);
	const mainLowerStartMm = anchorAtLowEnd(anchor)
		? upperDepthMm
		: lower.startMm;
	return [
		upper,
		{
			...lower,
			startMm: mainLowerStartMm,
			lengthMm: Math.max(0, lower.startMm + lower.lengthMm - mainLowerStartMm),
		},
		sideUpper,
		sideLower,
	];
}

export function createBenchRunsForVariant(
	variant: InteriorLayoutVariantId,
	room: InteriorRoom,
	anchor: BenchAnchor = DEFAULT_BENCH_ANCHOR,
): BenchRun[] {
	const upper = fullWallRun(
		room,
		anchor.wall,
		"upper",
		UPPER_BENCH_DEPTH_MM,
		anchor.offsetFromWallMm,
		0,
		anchor.endMm,
	);
	const lower = fullWallRun(
		room,
		anchor.wall,
		"lower",
		LOWER_BENCH_DEPTH_MM,
		anchor.offsetFromWallMm + UPPER_BENCH_DEPTH_MM,
		0,
		anchor.endMm,
	);
	const [firstSide, secondSide] = sideWallsFor(anchor);
	// The band the side runs must clear is the DEEPEST main run — the foot lav
	// standing in front of the upper lav, not the upper lav itself.
	const bandMm = sideRunBandMm([upper, lower]);

	switch (variant) {
		case "straight":
			return [upper];
		case "two-tier":
			return [upper, lower];
		case "l-shape":
			return anchor.wrapTiers
				? wrappedLShape(room, anchor, upper, lower)
				: [upper, lower, sideRun(room, firstSide, anchor, bandMm)];
		case "u-shape":
			return [
				upper,
				lower,
				sideRun(room, firstSide, anchor, bandMm),
				sideRun(room, secondSide, anchor, bandMm),
			];
	}
}

export interface CreateInteriorLayoutOptions {
	variant?: InteriorLayoutVariantId;
	/**
	 * Where to stand the heater. Defaults to the middle of the engines' right
	 * wall; the engines pass the position the placement domain actually picked,
	 * so the generated layout matches the model.
	 */
	heater?: Partial<HeaterPlacement>;
	/** Which wall the benches hang on. See `BenchAnchor`. */
	benchAnchor?: BenchAnchor;
	/**
	 * Runs the engine draws that the variants do not describe — today the
	 * two-room relax bench, which belongs to a different room entirely.
	 */
	extraRuns?: BenchRun[];
}

/**
 * Generate a complete starting layout for a room. Everything is derived from
 * the room's measurements — no fixed table of "presets by size".
 */
export function createInteriorLayout(
	room: InteriorRoom,
	options: CreateInteriorLayoutOptions = {},
): InteriorLayout {
	const variant = options.variant ?? "two-tier";
	const wall = options.heater?.wall ?? "right";
	const heater: HeaterPlacement = {
		wall,
		offsetMm: options.heater?.offsetMm ?? wallAxisLengthMm(room, wall) / 2,
		wallGapMm: options.heater?.wallGapMm ?? DEFAULT_HEATER_WALL_GAP_MM,
	};

	return {
		variant,
		benchRuns: [
			...createBenchRunsForVariant(variant, room, options.benchAnchor),
			...(options.extraRuns ?? []),
		],
		heater,
	};
}
